import { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { request } from '@/lib/trackizy';
import dayjs from 'dayjs';

/**
 * Pair raw position rows into visit segments.
 * A visit = contiguous run of positions inside POI radius.
 * Gap > 15 minutes between consecutive positions = new visit.
 */
export function pairPositionsIntoVisits(positions, gapMinutes = 15) {
  if (!positions.length) return [];
  const visits = [];
  let currentVisit = null;

  for (const pos of positions) {
    const time = dayjs(pos.fix_time);
    if (!currentVisit) {
      currentVisit = {
        deviceId: pos.traccar_device_id,
        enterTime: pos.fix_time,
        exitTime: pos.fix_time,
        positions: [pos],
      };
    } else if (
      pos.traccar_device_id === currentVisit.deviceId &&
      time.diff(dayjs(currentVisit.exitTime), 'minute') <= gapMinutes
    ) {
      // Continue current visit
      currentVisit.exitTime = pos.fix_time;
      currentVisit.positions.push(pos);
    } else {
      // Close current visit, start new one
      currentVisit.durationMinutes = dayjs(currentVisit.exitTime).diff(dayjs(currentVisit.enterTime), 'minute');
      visits.push(currentVisit);
      currentVisit = {
        deviceId: pos.traccar_device_id,
        enterTime: pos.fix_time,
        exitTime: pos.fix_time,
        positions: [pos],
      };
    }
  }
  if (currentVisit) {
    currentVisit.durationMinutes = dayjs(currentVisit.exitTime).diff(dayjs(currentVisit.enterTime), 'minute');
    visits.push(currentVisit);
  }
  return visits;
}

/**
 * Fetch historical visit stats for a single POI.
 *
 * @param {Object} poi - POI object with id, latitude, longitude, radius_meters
 * @param {{ from: string, to: string }|null} dateRange - ISO date strings; defaults to last 7 days
 * @returns {{ visits: Array, loading: boolean, refetch: Function }}
 */
export default function useVisitStats(poi, dateRange) {
  const [visits, setVisits] = useState([]);
  const [loading, setLoading] = useState(false);
  const orgId = useSelector((state) => state.org?.id);

  const fetchVisits = useCallback(async () => {
    if (!orgId || !poi) return;
    setLoading(true);
    try {
      const from = dateRange?.from || dayjs().subtract(7, 'day').toISOString();
      const to = dateRange?.to || dayjs().toISOString();
      const data = await request(`/pois/${poi.id}/visits?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`);
      if (Array.isArray(data)) {
        setVisits(pairPositionsIntoVisits(data));
      }
    } catch {
      // Silently fail
    } finally {
      setLoading(false);
    }
  }, [orgId, poi?.id, dateRange?.from, dateRange?.to]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchVisits(); }, [fetchVisits]);

  return { visits, loading, refetch: fetchVisits };
}

/**
 * Fetch visit counts for ALL POIs in the org (last 7 days).
 * Returns { counts: { [poiId]: { count } }, loading }.
 */
export function useAllPOIVisitCounts(days = 7) {
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(false);
  const orgId = useSelector((state) => state.org?.id);
  const pois = useSelector((state) => state.pois?.items);

  useEffect(() => {
    if (!orgId || !pois) return;
    const poiList = Object.values(pois);
    if (!poiList.length) return;

    let cancelled = false;
    setLoading(true);

    (async () => {
      const from = dayjs().subtract(days, 'day').toISOString();
      const to = dayjs().toISOString();
      const result = {};

      // Batch API calls — one per POI (capped at 50 to avoid flooding)
      const batch = poiList.slice(0, 50);
      await Promise.all(batch.map(async (poi) => {
        try {
          const data = await request(`/pois/${poi.id}/visits?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`);
          if (Array.isArray(data) && !cancelled) {
            result[poi.id] = { count: pairPositionsIntoVisits(data).length };
          }
        } catch {
          // Skip failed POI
        }
      }));

      if (!cancelled) {
        setCounts(result);
        setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [orgId, Object.keys(pois || {}).length, days]); // eslint-disable-line react-hooks/exhaustive-deps

  return { counts, loading };
}
